import styled from "@emotion/styled"
import { PText } from "@porsche-design-system/components-react"
import {
	colorContrastLow,
	colorPrimary,
	durationMd,
	easeInOut,
	radiusMd,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import { shouldForwardProp } from "src/common/shouldForwardProp"

export interface ProgressBarProps {
	className?: string
	/**
	 * Progress in percent (0–100). When omitted, the bar renders in an indeterminate
	 * "loading" state, so this component can be wired in before an API returns real progress.
	 */
	value?: number
}

export const ProgressBar = ({ className, value }: ProgressBarProps) => {
	const clampedValue = value === undefined ? undefined : Math.min(100, Math.max(0, value))

	return (
		<Wrapper className={className}>
			<Track role="progressbar" aria-valuenow={clampedValue} aria-valuemin={0} aria-valuemax={100}>
				<Indicator $indeterminate={clampedValue === undefined} $value={clampedValue ?? 0} />
			</Track>
			{clampedValue !== undefined && (
				<PText size="xs" color="contrast-medium">
					{Math.round(clampedValue)}%
				</PText>
			)}
		</Wrapper>
	)
}

const Wrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticSm};
`

const Track = styled.div`
	flex: 1;
	block-size: 6px;
	border-radius: ${radiusMd};
	background-color: ${colorContrastLow};
	overflow: hidden;
`

const Indicator = styled("div", { shouldForwardProp })<{ $indeterminate: boolean; $value: number }>`
	block-size: 100%;
	border-radius: ${radiusMd};
	background-color: ${colorPrimary};
	transition: inline-size ${durationMd} ${easeInOut};

	${({ $indeterminate, $value }) =>
		$indeterminate
			? `
		inline-size: 40%;
		animation: indeterminate-progress 1.2s ${easeInOut} infinite;

		@keyframes indeterminate-progress {
			0% {
				transform: translateX(-100%);
			}
			100% {
				transform: translateX(250%);
			}
		}
	`
			: `inline-size: ${$value}%;`}
`
